import { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * PushNotificationManager - Handles Web Push notification permissions and scheduling.
 * 
 * Features:
 * - Request notification permission
 * - Schedule daily streak reminders at user-preferred time
 * - Persist settings to localStorage
 */

const STORAGE_KEY = 'gl_push_settings';

// Default reminder time: 6:00 PM
const DEFAULT_REMINDER_HOUR = 18;
const DEFAULT_REMINDER_MINUTE = 0;

function getStoredSettings() {
  try {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveSettings(settings) {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage may be full or unavailable
  }
}

export function usePushNotifications() {
  const [permission, setPermission] = useState('default');
  const [enabled, setEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState({ hour: DEFAULT_REMINDER_HOUR, minute: DEFAULT_REMINDER_MINUTE });

  useEffect(() => {
    // Check current permission status - with safety check
    if (typeof Notification !== 'undefined' && 'Notification' in window) {
      setPermission(Notification.permission);
    }

    // Load stored settings
    const settings = getStoredSettings();
    if (settings) {
      const notificationGranted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
      setEnabled(settings.enabled && notificationGranted);
      if (settings.reminderTime) {
        setReminderTime(settings.reminderTime);
      }
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined' || !('Notification' in window)) {
      toast.error('Push notifications not supported in this browser');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        setEnabled(true);
        saveSettings({ enabled: true, reminderTime });
        toast.success('Notifications enabled! We\'ll remind you to protect your streak.');
        
        // Schedule a test notification
        scheduleNotification('Welcome to Get Green Light!', 'You\'ll get reminders to protect your streak.', 2000);
        
        return true;
      } else if (result === 'denied') {
        toast.error('Notifications blocked. Enable them in browser settings.');
        return false;
      }
      return false;
    } catch (err) {
      console.error('Notification permission error:', err);
      toast.error('Could not enable notifications');
      return false;
    }
  }, [reminderTime]);

  const disableNotifications = useCallback(() => {
    setEnabled(false);
    saveSettings({ enabled: false, reminderTime });
    toast.success('Notifications disabled');
  }, [reminderTime]);

  const updateReminderTime = useCallback((hour, minute) => {
    const newTime = { hour, minute };
    setReminderTime(newTime);
    saveSettings({ enabled, reminderTime: newTime });
  }, [enabled]);

  return {
    permission,
    enabled,
    reminderTime,
    requestPermission,
    disableNotifications,
    updateReminderTime,
    isSupported: 'Notification' in window
  };
}

/**
 * Schedule a notification to appear after a delay.
 */
export function scheduleNotification(title, body, delayMs = 0) {
  // Safety check for Notification API
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

  setTimeout(() => {
    try {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'greenlight-reminder',
        requireInteraction: false,
        silent: false
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto-close after 10 seconds
      setTimeout(() => notification.close(), 10000);
    } catch (err) {
      console.error('Failed to show notification:', err);
    }
  }, delayMs);
}

/**
 * Check and trigger streak reminder if needed.
 * Call this when the app loads or becomes visible.
 */
export function checkStreakReminder(hasScannedToday, streakDays) {
  // Safety check - Notification API may not be available on all browsers/devices
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  
  const settings = getStoredSettings();
  if (!settings?.enabled) return;

  const now = new Date();
  const hour = now.getHours();
  const { hour: reminderHour } = settings.reminderTime || { hour: DEFAULT_REMINDER_HOUR };

  // Only trigger between reminder time and midnight
  if (hour >= reminderHour && hour < 23 && !hasScannedToday && streakDays > 0) {
    // Check if we already sent a reminder today
    const lastReminder = localStorage.getItem('gl_last_reminder_date');
    const today = now.toDateString();
    
    if (lastReminder !== today) {
      localStorage.setItem('gl_last_reminder_date', today);
      scheduleNotification(
        `🔥 Protect your ${streakDays}-day streak!`,
        'Scan something before midnight to keep your streak alive.',
        1000
      );
    }
  }
}

/**
 * PushNotificationPrompt - UI component to enable notifications
 */
export function PushNotificationPrompt({ onClose }) {
  const { permission, enabled, requestPermission, isSupported } = usePushNotifications();

  if (!isSupported || permission === 'denied' || enabled) {
    return null;
  }

  return (
    <div 
      className="bg-qc-surface border border-qc-border rounded-2xl p-4 mb-4 relative"
      style={{ zIndex: 10 }}
      data-testid="push-notification-prompt"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-qc-green/15 border border-qc-green/30 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-qc-green" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-qc-text mb-1">Never break your streak</p>
          <p className="text-xs text-qc-text-dim mb-3">
            Get a friendly reminder each evening to scan before your day ends.
          </p>
          <div className="flex items-center gap-2">
            <Button
              onClick={requestPermission}
              size="sm"
              data-testid="enable-notifications-btn"
              className="h-8 px-4 bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-full font-bold text-xs"
            >
              Enable Reminders
            </Button>
            <button
              onClick={onClose}
              data-testid="push-notification-not-now"
              className="text-xs text-qc-text-faint hover:text-qc-text-dim"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="push-notification-close"
          className="text-qc-text-faint hover:text-qc-text-dim p-1"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * NotificationSettings - Settings panel for notification preferences
 */
export function NotificationSettings() {
  const { 
    permission, 
    enabled, 
    reminderTime, 
    requestPermission, 
    disableNotifications, 
    updateReminderTime,
    isSupported 
  } = usePushNotifications();

  if (!isSupported) {
    return (
      <div className="text-sm text-qc-text-dim">
        Push notifications are not supported in this browser.
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="flex items-center gap-3 p-4 bg-qc-surface rounded-xl border border-qc-border">
        <BellOff className="w-5 h-5 text-qc-text-faint" />
        <div>
          <p className="text-sm font-medium text-qc-text">Notifications Blocked</p>
          <p className="text-xs text-qc-text-dim">Enable them in your browser settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 bg-qc-surface rounded-xl border border-qc-border">
        <div className="flex items-center gap-3">
          <Bell className={`w-5 h-5 ${enabled ? 'text-qc-green' : 'text-qc-text-faint'}`} />
          <div>
            <p className="text-sm font-medium text-qc-text">Streak Reminders</p>
            <p className="text-xs text-qc-text-dim">Daily reminder to protect your streak</p>
          </div>
        </div>
        <Button
          onClick={enabled ? disableNotifications : requestPermission}
          variant={enabled ? "outline" : "default"}
          size="sm"
          data-testid="toggle-notifications-btn"
          className={enabled 
            ? "h-8 px-3 border-qc-border text-qc-text-dim" 
            : "h-8 px-3 bg-qc-green text-qc-bg"
          }
        >
          {enabled ? 'Disable' : 'Enable'}
        </Button>
      </div>

      {enabled && (
        <div className="flex items-center justify-between p-4 bg-qc-surface rounded-xl border border-qc-border">
          <div>
            <p className="text-sm font-medium text-qc-text">Reminder Time</p>
            <p className="text-xs text-qc-text-dim">When to remind you daily</p>
          </div>
          <select
            value={`${reminderTime.hour}:${reminderTime.minute.toString().padStart(2, '0')}`}
            onChange={(e) => {
              const [h, m] = e.target.value.split(':').map(Number);
              updateReminderTime(h, m);
            }}
            data-testid="reminder-time-select"
            className="h-8 px-3 bg-qc-surface2 border border-qc-border rounded-lg text-sm text-qc-text"
          >
            <option value="17:00">5:00 PM</option>
            <option value="18:00">6:00 PM</option>
            <option value="19:00">7:00 PM</option>
            <option value="20:00">8:00 PM</option>
            <option value="21:00">9:00 PM</option>
          </select>
        </div>
      )}
    </div>
  );
}
